import { useState, useEffect, useCallback, TouchEvent } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Collapse,
  Paper,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Attachment } from '../hooks/useHomeAttachments';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../hooks/useChat';
import { useImageEngagementTracking } from '../hooks/useImageEngagementTracking';

interface ImageViewerProps {
  open: boolean;
  onClose: () => void;
  imageUrl?: string | null; // For backward compatibility
  images?: string[]; // For backward compatibility
  attachments?: Attachment[]; // New prop for attachments with assessment data
  initialIndex?: number;
  onDelete?: (attachment: Attachment) => Promise<boolean | { success: boolean; remainingCount: number }>;
}

const ImageViewer = ({
  open,
  onClose,
  imageUrl = null,
  images = [],
  attachments = [],
  initialIndex = 0,
  onDelete
}: ImageViewerProps) => {
  // Get auth and chat contexts for analytics
  const { user } = useAuth();
  const { homeId } = useChat();
  
  // Determine which data source to use
  const hasAttachments = attachments.length > 0;
  const imageList = hasAttachments
    ? attachments.map(att => att.image_url || (homeId ? `/api/homes/${homeId}/attachment/${att.key}` : ''))
    : (images.length > 0 ? images : (imageUrl ? [imageUrl] : []));

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showAssessment, setShowAssessment] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Image engagement tracking hook
  const { logEngagement } = useImageEngagementTracking({
    userUuid: user?.uuid,
    homeId: homeId || undefined,
    currentImageUrl: imageList[currentIndex],
    attachments: hasAttachments ? attachments : undefined,
    isVisible: open
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Touch swipe state variables
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance to register as swipe (in pixels)
  const minSwipeDistance = 50;

  // Reset current index when modal opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setShowAssessment(true);
      setShowDeleteConfirm(false);
    }
  }, [open, initialIndex]);

  const handlePrevious = useCallback(() => {
    logEngagement('next_image');
    const newIndex = (currentIndex - 1 + imageList.length) % imageList.length;
    setCurrentIndex(newIndex);
  }, [currentIndex, imageList.length, logEngagement]);

  const handleNext = useCallback(() => {
    logEngagement('next_image');
    const newIndex = (currentIndex + 1) % imageList.length;
    setCurrentIndex(newIndex);
  }, [currentIndex, imageList.length, logEngagement]);

  const toggleAssessment = () => {
    setShowAssessment(prev => !prev);
  };

  const handleDeleteClick = () => {
    setDeleteError(null);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete || !currentAttachment) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const result = await onDelete(currentAttachment);
      const success = typeof result === 'object' ? result.success : result;
      if (success) {
        setShowDeleteConfirm(false);
        const remaining = typeof result === 'object' ? result.remainingCount : attachments.length - 1;
        if (remaining <= 0) {
          onClose();
        } else if (currentIndex >= remaining) {
          setCurrentIndex(Math.max(0, remaining - 1));
        }
      } else {
        setDeleteError('Failed to delete photo. Please try again.');
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete photo');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  // Get current attachment if using attachments
  const currentAttachment = hasAttachments && currentIndex < attachments.length ? 
    attachments[currentIndex] : null;
  const hasAssessment = Boolean(currentAttachment && 
    (currentAttachment.imagePros || currentAttachment.imageCons || currentAttachment.imageOverall));

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;
      
      if (event.key === 'ArrowLeft') {
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      } else if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, handlePrevious, handleNext]);

  // Handle touch start event
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // Handle touch end event
  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (!touchStart) return;
    setTouchEnd(e.changedTouches[0].clientX);
  };

  // Process swipe when touchEnd changes
  useEffect(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance < 0) {
        handlePrevious();
      } else {
        handleNext();
      }
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, handlePrevious, handleNext]);

  if (imageList.length === 0) return null;

  // Only adjust layout when assessment panel is explicitly shown
  // Both contentJustify and imageContainerHeight should be normal by default
  const contentJustify = isMobile && showAssessment ? 'flex-start' : 'center';
  const imageContainerHeight = isMobile && showAssessment ? '55%' : '100%';
  const panelHeight = isMobile ? '45%' : '40%';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xl"
      sx={{ 
        zIndex: 2100
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          boxShadow: 'none',
          m: 0,
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: '100%'
        }
      }}
    >
      <DialogContent 
        sx={{ 
          p: 0, 
          display: 'flex',
          position: 'relative',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: contentJustify,
          overflow: 'hidden',
          height: '100%',
          width: '100%',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <Box 
          sx={{ 
            position: 'relative', 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '100%',
            maxHeight: imageContainerHeight,
            width: '100%',
            height: imageContainerHeight,
            transition: 'all 0.3s ease-in-out',
            pt: isMobile && showAssessment ? 2 : 0,
            overflow: 'hidden'
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close button */}
          <IconButton
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              color: 'white',
              zIndex: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              m: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)'
              }
            }}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>

          {/* Delete button */}
          {onDelete && currentAttachment && (
            <IconButton
              data-testid="delete-photo-button"
              sx={{
                position: 'absolute',
                top: 0,
                right: 48,
                color: 'white',
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                m: 1,
                '&:hover': {
                  backgroundColor: 'rgba(183, 28, 28, 0.7)'
                }
              }}
              onClick={handleDeleteClick}
            >
              <DeleteOutlineIcon />
            </IconButton>
          )}
          
          {/* Show assessment info button */}
          {hasAssessment && (
            <IconButton
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                color: 'white',
                zIndex: 10,
                backgroundColor: showAssessment ? 'rgba(25, 118, 210, 0.6)' : 'rgba(0, 0, 0, 0.3)',
                m: 1,
                '&:hover': {
                  backgroundColor: showAssessment ? 'rgba(25, 118, 210, 0.8)' : 'rgba(0, 0, 0, 0.5)'
                }
              }}
              onClick={toggleAssessment}
            >
              <InfoIcon />
            </IconButton>
          )}
          
          {/* Current image */}
          <img
            src={imageList[currentIndex]}
            alt="Enlarged view"
            style={{
              width: 'auto',
              height: 'auto',
              maxWidth: '100%', // Increased from 95% to 100%
              maxHeight: '100%', // Increased from 95% to 100%
              objectFit: 'contain',
              objectPosition: isMobile && showAssessment ? 'center top' : 'center center',
              transition: 'all 0.3s ease-in-out'
            }}
          />
          
          {/* Navigation arrows - only show if we have multiple images */}
          {imageList.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: { xs: 8, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 10,
                  borderRadius: '50%',
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '1.8rem', sm: '2.2rem' },
                    filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))'
                  }
                }}
              >
                <ChevronLeftIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              </IconButton>
              
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: { xs: 8, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 10,
                  borderRadius: '50%',
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '1.8rem', sm: '2.2rem' },
                    filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))'
                  }
                }}
              >
                <ChevronRightIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              </IconButton>
            </>
          )}
          
          {/* Image counter indicator */}
          {imageList.length > 1 && !showAssessment && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: 16,
                fontSize: '0.875rem',
                zIndex: 5
              }}
            >
              {currentIndex + 1} / {imageList.length}
            </Box>
          )}
        </Box>
          
        {/* Assessment Panel - Overlay at the bottom */}
        <Collapse 
          in={Boolean(showAssessment && hasAssessment)} 
          sx={{
            position: isMobile ? 'relative' : 'absolute',
            bottom: 0,
            left: 0,
            right: 0, 
            width: '100%',
            height: isMobile ? panelHeight : 'auto',
            maxHeight: panelHeight,
            overflowY: 'auto',
            zIndex: 5,
            mt: isMobile ? 1 : 0
          }}
        >
          <Paper 
            sx={{ 
              p: 2, 
              bgcolor: 'rgba(0, 0, 0, 0.85)', 
              color: 'white',
              borderRadius: { xs: 0, sm: '16px 16px 0 0' },
              backdropFilter: 'blur(5px)',
              height: '100%'
            }}
            elevation={8}
          >
            <Typography variant="h6" gutterBottom color="primary">
              Image Analysis
            </Typography>
            
            {currentAttachment?.imageOverall && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1">{currentAttachment.imageOverall}</Typography>
              </Box>
            )}
            
            <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              {currentAttachment?.imagePros && (
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ThumbUpIcon fontSize="small" sx={{ color: 'success.main', mr: 1 }} />
                    <Typography variant="subtitle2" color="success.light">Positive Aspects</Typography>
                  </Box>
                  <Typography variant="body2">{currentAttachment.imagePros}</Typography>
                </Box>
              )}
              
              {currentAttachment?.imageCons && (
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ThumbDownIcon fontSize="small" sx={{ color: 'error.main', mr: 1 }} />
                    <Typography variant="subtitle2" color="error.light">Concerns</Typography>
                  </Box>
                  <Typography variant="body2">{currentAttachment.imageCons}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Collapse>
      </DialogContent>

      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <Box
          data-testid="delete-confirm-dialog"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 20
          }}
          onClick={handleDeleteCancel}
        >
          <Paper
            sx={{ p: 3, maxWidth: 320, textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" gutterBottom>Delete Photo</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This photo will be permanently deleted. This action cannot be undone.
            </Typography>
            {deleteError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {deleteError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-delete-button"
                variant="contained"
                color="error"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Dialog>
  );
};

export default ImageViewer;